import { Chip, Stack, Tooltip, Typography } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { pluralWord } from '../../shared/plural';

/**
 * Komórki ekranu spółek.
 *
 * Osobno od `CompanyList.tsx` z tego samego powodu co w banku zleceń: te
 * komponenty biorą rekord w propsie i nie potrzebują react-admina, więc dają
 * się testować bez całej ramy (pełny kontekst ciągnie router, którego Jest
 * w tym monorepo nie rozwiązuje).
 */

/** Rekord z odpowiedzi API — luźny celowo, bo pola bywają puste. */
export type LooseRecord = Record<string, unknown>;

const asText = (value: unknown): string | undefined =>
  typeof value === 'string' && value !== '' ? value : undefined;

/** Etykiety trybów maskowania — te same kody co w `publikacje/bramki.ts`. */
export const MASKING_LABELS: Record<string, string> = {
  dokladna: 'Dokładna lokalizacja',
  'kod-skrocony': 'Skrócony kod pocztowy',
  'tylko-miasto': 'Tylko miasto',
  // Odpowiedzi Marathonu/Euvic 16.10.2026, pyt. 19 — ustawić da się tylko
  // z przełącznikiem `maskingNearbyLocality`; etykieta zawsze, żeby
  // zapisany tryb dało się odczytać.
  przyblizony: 'Przybliżony (miejscowość w pobliżu)',
};

/**
 * Tryb maskowania lokalizacji przy publikacji.
 *
 * Brak trybu **nie jest błędem konfiguracji**: wtedy operator wybiera maskę
 * przy każdej publikacji i nic nie idzie „dokładną" po cichu (I.09). Pusta
 * komórka czytałaby się jako „nic nie ustawiono, pewnie leci domyślnie",
 * więc piszemy wprost, co się wtedy dzieje.
 */
export const MaskingCell = ({ record }: { record: LooseRecord }) => {
  const mode = asText(record.locationMasking);
  if (!mode) {
    return (
      <Tooltip title="Bez ustawionego trybu operator wybiera maskę przy każdej publikacji — nic nie idzie „dokładną” po cichu">
        <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
          <WarningAmberIcon fontSize="small" color="warning" />
          <Typography variant="body2">wybór przy publikacji</Typography>
        </Stack>
      </Tooltip>
    );
  }
  return (
    <Chip
      size="small"
      variant="outlined"
      label={MASKING_LABELS[mode] ?? mode}
    />
  );
};

/** Identyfikator spółki w komunikatach VIA — po nim rozpoznajemy ją w danych klienta. */
export const ViaIdCell = ({ record }: { record: LooseRecord }) => (
  <span>{asText(record.viaCompanyId) ?? '—'}</span>
);

const formatCount = (value: unknown): string =>
  typeof value === 'number' ? value.toLocaleString('pl-PL') : '—';

/** Ile floty i zleceń wisi przy spółce — skala, nie szczegóły. */
export const ResourcesCell = ({ record }: { record: LooseRecord }) => {
  const c = record._count as { cars?: number; userOffers?: number } | undefined;
  // Odmiana jak w innych licznikach (07.10.2026): „7 003 zlecenia”; bez
  // liczby forma „zleceń” przy kresce.
  const orders = typeof c?.userOffers === 'number' ? pluralWord(c.userOffers, 'zlecenie', 'zlecenia', 'zleceń') : 'zleceń';
  return (
    <span>
      {formatCount(c?.cars)} poj. · {formatCount(c?.userOffers)} {orders}
    </span>
  );
};

export const NameCell = ({ record }: { record: LooseRecord }) => (
  <strong>{asText(record.name) ?? '—'}</strong>
);

export const CountryCell = ({ record }: { record: LooseRecord }) => (
  <span>{asText(record.country) ?? '—'}</span>
);
